import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Crown, Flame, Sparkles, User, LogOut, ChevronUp, ChevronDown, Wrench } from "lucide-react";
import { toast } from "sonner";

export default function DevAccountSwitcher() {
  const { user, setDevUser } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  // Only render on localhost
  const isLocal = typeof window !== "undefined" && (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname.startsWith("192.168.")
  );

  if (!isLocal) return null;

  const currentRole = user ? user.role : "guest";

  const handleSwitch = (account) => {
    if (account === null) {
      setDevUser(null);
      toast.info("Switched to Guest / Logged Out");
    } else {
      setDevUser(account);
      toast.success(`Switched to: ${account.name} (${account.role})`);
    }
  };

  const ACCOUNTS = [
    {
      role: "admin",
      label: "👑 Founder",
      badgeClass: "from-amber-600 to-rose-700 text-white",
      data: {
        id: "dev_founder_001",
        name: "Kyouka Reimei",
        email: "founder@kyouka.org",
        role: "admin",
        badges: ["cult_master"],
        custom_title: null,
        avatar_url: "",
      },
    },
    {
      role: "hardcore_cultist",
      label: "🛡️ Alvan (Veteran)",
      badgeClass: "from-rose-600 to-red-800 text-white",
      data: {
        id: "dev_hardcore_002",
        name: "Alvan Hardcore",
        email: "hardcore@cult.org",
        role: "hardcore_cultist",
        badges: [],
        custom_title: "Veteran Cultist 🛡️",
        avatar_url: "",
      },
    },
    {
      role: "god_tier_supporter",
      label: "💎 Reza (God Tier)",
      badgeClass: "from-amber-500 to-yellow-700 text-white",
      data: {
        id: "dev_god_003",
        name: "Reza Cultist",
        email: "reza@cult.org",
        role: "god_tier_supporter",
        badges: ["top_1_supporter"],
        custom_title: "Pilar Sekte 💎",
        avatar_url: "",
      },
    },
    {
      role: "elite_cultist",
      label: "⚡ Elite Cultist",
      badgeClass: "from-blue-600 to-indigo-800 text-white",
      data: {
        id: "dev_elite_004",
        name: "Rian Elite",
        email: "elite@cult.org",
        role: "elite_cultist",
        badges: [],
        custom_title: null,
        avatar_url: "",
      },
    },
    {
      role: "member",
      label: "👤 Free Member",
      badgeClass: "from-zinc-700 to-zinc-900 text-zinc-300",
      data: {
        id: "dev_member_005",
        name: "Novice Cultist",
        email: "free@cult.org",
        role: "member",
        badges: [],
        custom_title: null,
        avatar_url: "",
      },
    },
  ];

  return (
    <div className="fixed bottom-4 left-4 z-50 flex flex-col items-start font-sans">
      <div className="rounded-2xl bg-zinc-950/95 border border-rose-500/40 p-2.5 shadow-2xl shadow-rose-950/60 backdrop-blur-xl text-white transition-all">
        {/* Header toolbar */}
        <div className="flex items-center justify-between gap-3 px-1.5 pb-2 border-b border-zinc-800">
          <div className="flex items-center gap-1.5 text-xs font-bold text-rose-300">
            <Wrench className="w-3.5 h-3.5 text-rose-400" />
            <span>Dev Switcher (Localhost)</span>
          </div>

          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
            title={collapsed ? "Buka Menu" : "Tutup Menu"}
          >
            {collapsed ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>

        {/* Current status info */}
        <div className="px-1.5 py-1 text-[11px] text-zinc-400 flex items-center justify-between gap-2 flex-wrap">
          <span>Status Akun:</span>
          <span className="font-bold text-zinc-200 flex items-center gap-1.5 flex-wrap">
            {user ? (
              <>
                <span>{user.name}</span>
                <span className="text-[10px] text-zinc-400 uppercase">({user.role})</span>
                {user.custom_title && (
                  <span className="px-1.5 py-0.5 rounded-full bg-amber-500/25 border border-amber-400/60 text-amber-200 text-[10px] font-extrabold shadow-sm">
                    🌟 {user.custom_title}
                  </span>
                )}
              </>
            ) : (
              "Tamu (Belum Login)"
            )}
          </span>
        </div>

        {/* Role buttons */}
        {!collapsed && (
          <div className="mt-1.5 flex flex-wrap gap-1.5 max-w-xs">
            {ACCOUNTS.map((acc) => {
              const isActive = currentRole === acc.role;
              return (
                <button
                  key={acc.role}
                  type="button"
                  onClick={() => handleSwitch(acc.data)}
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded-xl transition-all active:scale-95 border ${
                    isActive
                      ? "bg-rose-600 border-rose-400 text-white shadow-md shadow-rose-950 ring-1 ring-rose-400"
                      : "bg-zinc-900/80 hover:bg-zinc-800 border-zinc-700/80 text-zinc-300 hover:text-white"
                  }`}
                >
                  {acc.label} {isActive && "✓"}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => handleSwitch(null)}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-xl transition-all active:scale-95 border ${
                currentRole === "guest"
                  ? "bg-zinc-800 border-zinc-600 text-white"
                  : "bg-zinc-900/60 hover:bg-zinc-800 border-zinc-700/60 text-zinc-400 hover:text-white"
              }`}
            >
              🚪 Logout {currentRole === "guest" && "✓"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
